import produce from 'immer';

import type {
  FilterVariables,
  FiltersDataConfig,
  NormalizedTournamentsResponse,
  TableFilters,
  TableSortRule
} from '../../../types/MTT';
import { filterMTTTournaments, stateTimeComparator } from '../../../utils/MTT';
import type { PokerMultiTableTournamentsStateBase as State } from '../types';

import updateTournament from './updateTournament';

const clear = <T extends State>(defaultState: T) => produce(defaultState, (draft) => draft);

const setActiveFilters = <T extends State>(state: T, payload: FilterVariables) => ({
  ...state,
  activeFilters: { ...state.activeFilters, ...payload },
  selectedTournamentId: 0
});

const setFiltersData = <T extends State>(state: T, payload: FiltersDataConfig) => ({
  ...state,
  filtersData: payload
});

const setFiltersError = <T extends State>(state: T, payload: string) => ({
  ...state,
  filtersData: { RM: [], PM: [] },
  filtersError: payload
});

const setHasError = <T extends State>(state: T, payload: boolean) => ({
  ...state,
  hasError: payload
});

const setSelectedTournamentId = <T extends State>(state: T, payload: number) => ({
  ...state,
  selectedTournamentId: payload
});

const setTableFilters = <T extends State>(state: T, payload: TableFilters) => ({
  ...state,
  tableFilters: payload,
  selectedTournamentId: 0
});

const setTableRowsCount = <T extends State>(state: T, payload: number) => ({
  ...state,
  tableRowsCount: payload
});

const setTableSortRule = <T extends State>(state: T, payload: TableSortRule) => ({
  ...state,
  tableSortRule: payload
});

const setTournamentsLoading = <T extends State>(state: T) => ({
  ...state,
  tournamentsData: {},
  tournamentsCount: 0,
  tableRowsCount: 0,
  isLoading: true
});

const setSubscriptionError = <T extends State>(state: T, payload: string) => ({
  ...state,
  subscriptionError: payload,
  isLoading: false
});

const tournamentsLoadFailure = <T extends State>(state: T, payload: string) => ({
  ...state,
  isLoading: false,
  tournamentsError: payload
});

export const tournamentsLoadSuccess = <T extends State>(
  state: T,
  payload: NormalizedTournamentsResponse
) => {
  const tournamentsList = filterMTTTournaments(
    state.activeFilters,
    Object.values(payload.tournamentsData)
  ).sort(stateTimeComparator);

  return {
    ...state,
    ...payload,
    tableRowsCount: tournamentsList.length,
    selectedTournamentId: tournamentsList[0]?.id ?? 0,
    isLoading: false
  };
};

export const generateMTTReducer = <T extends State>(defaultState: T) => ({
  clear: () => clear<T>(defaultState),
  setActiveFilters: setActiveFilters<T>,
  setFiltersData: setFiltersData<T>,
  setFiltersError: setFiltersError<T>,
  setHasError: setHasError<T>,
  setSelectedTournamentId: setSelectedTournamentId<T>,
  setSubscriptionError: setSubscriptionError<T>,
  setTableFilters: setTableFilters<T>,
  setTableRowsCount: setTableRowsCount<T>,
  setTableSortRule: setTableSortRule<T>,
  setTournamentsLoading: setTournamentsLoading<T>,
  tournamentsLoadFailure: tournamentsLoadFailure<T>,
  tournamentsLoadSuccess: tournamentsLoadSuccess<T>,
  updateTournament: updateTournament<T>
});
